export const localStorageHelper = {
  setAItokenLeft: (tokenCount) => {
    try {
      return window.localStorage.setItem(
        "AITokenLeft",
        JSON.stringify(tokenCount)
      );
    } catch (e) {
      console.error("Error setting AITokenLeft to local storage:", e);
      return null;
    }
  },

  getAItokenLeft: () => {
    try {
      return JSON.parse(window.localStorage.getItem("AITokenLeft"));
    } catch (e) {
      console.error("Error retrieving AITokenLeft from local storage:", e);
      return 0;
    }
  },
};

export const setDoNotShowDeletePopupAgainValue = (booleanValue = true) => {
  try {
    return window.localStorage.setItem(
      "DoNotShowDeletePopupAgainValue",
      JSON.stringify(booleanValue)
    );
  } catch (e) {
    console.error("Error setting doNotShowThisPopup to local storage:", e);
    return null;
  }
};

export const getDoNotShowDeletePopupAgainValue = () => {
  try {
    const value = window.localStorage.getItem("DoNotShowDeletePopupAgainValue");

    // If key doesn't exist or its value is null/undefined, return true as the default
    if (value === null || value === undefined) {
      return false;
    }

    return JSON.parse(value);
  } catch (e) {
    console.error(
      "Error retrieving showDeleteEmailsPopup from local storage:",
      e
    );
    return true; // Default to true in case of an error
  }
};

// ---------------------------------------------------------------------------
// Library/template segment palette-adoption preference (PAL-001).
//
// When a user adds a segment from the Segment Library, we prompt once asking
// whether the segment should adopt the current wheel's palette or keep the
// template's own colors. If the user ticks "Don't ask again", we remember the
// choice here (per-device) and apply it silently on future library adds.
//
// Shape persisted: { choice: "adopt" | "keep" }  (remembered) or null (ask).
// v1 is localStorage-only (per-device). A UserProfile field would make this
// cross-device — see the PR body. Reads/writes are defensive: any parse/quota
// error degrades to "no remembered preference" (i.e. prompt again), never throws.
// ---------------------------------------------------------------------------

export const LIBRARY_SEGMENT_PALETTE_CHOICE_KEY = "librarySegmentPaletteChoice";

export const LIBRARY_SEGMENT_PALETTE_CHOICES = Object.freeze({
  ADOPT: "adopt",
  KEEP: "keep",
});

export const setLibrarySegmentPaletteChoice = (choice) => {
  try {
    if (
      choice !== LIBRARY_SEGMENT_PALETTE_CHOICES.ADOPT &&
      choice !== LIBRARY_SEGMENT_PALETTE_CHOICES.KEEP
    ) {
      return null;
    }
    return window.localStorage.setItem(
      LIBRARY_SEGMENT_PALETTE_CHOICE_KEY,
      JSON.stringify({ choice })
    );
  } catch (e) {
    console.error(
      "Error setting librarySegmentPaletteChoice to local storage:",
      e
    );
    return null;
  }
};

// ---------------------------------------------------------------------------
// Chat "Delete for me" (WhatsApp-style, Gordon-locked 2026-08-12). The backend
// only exposes ONE delete endpoint — a tombstone visible to BOTH participants
// ("delete for everyone", `services/chat.js` `deleteMessage`). There is no
// server concept of "hidden for just this viewer", so "Delete for me" is a
// v1 PER-DEVICE, client-only hide: the message id is remembered here and
// filtered out of the thread on this device, the OTHER participant's copy and
// the server row are both untouched. Cross-device "delete for me" would need
// a real backend field — flagged as a BE follow-up in the PR body, not built
// here. Reads/writes are defensive (never throw), matching every other helper
// in this file.
// ---------------------------------------------------------------------------

export const CHAT_DELETED_FOR_ME_KEY = "chatDeletedForMeMessageIds";

/** All message ids the CURRENT device has "deleted for me", as strings. */
export const getDeletedForMeMessageIds = () => {
  try {
    const raw = window.localStorage.getItem(CHAT_DELETED_FOR_ME_KEY);
    if (raw === null || raw === undefined) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch (e) {
    console.error("Error retrieving chatDeletedForMeMessageIds from local storage:", e);
    return []; // Degrade to "nothing hidden" on any error.
  }
};

/** Remember a message id as "deleted for me" on this device (idempotent). */
export const addDeletedForMeMessageId = (messageId) => {
  try {
    const ids = getDeletedForMeMessageIds();
    const key = String(messageId);
    if (ids.includes(key)) return ids;
    const next = [...ids, key];
    window.localStorage.setItem(CHAT_DELETED_FOR_ME_KEY, JSON.stringify(next));
    return next;
  } catch (e) {
    console.error("Error setting chatDeletedForMeMessageIds to local storage:", e);
    return null;
  }
};

/** Whether `messageId` was previously "deleted for me" on this device. */
export const isMessageDeletedForMe = (messageId) =>
  getDeletedForMeMessageIds().includes(String(messageId));

// Returns "adopt" | "keep" when the user has chosen "Don't ask again",
// otherwise null (meaning: no remembered preference — show the prompt).
export const getLibrarySegmentPaletteChoice = () => {
  try {
    const raw = window.localStorage.getItem(LIBRARY_SEGMENT_PALETTE_CHOICE_KEY);
    if (raw === null || raw === undefined) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const choice = parsed?.choice;
    if (
      choice === LIBRARY_SEGMENT_PALETTE_CHOICES.ADOPT ||
      choice === LIBRARY_SEGMENT_PALETTE_CHOICES.KEEP
    ) {
      return choice;
    }
    return null;
  } catch (e) {
    console.error(
      "Error retrieving librarySegmentPaletteChoice from local storage:",
      e
    );
    return null; // Degrade to "prompt again" on any error.
  }
};
